import {
  DEFAULT_INTRO,
  formatMoney,
} from './accountingConstants';
import AccountingFindingNavigator from './AccountingFindingNavigator';
import CorrectionCaseLog from './CorrectionCaseLog';

export default function AccountingScanSection({
  intro,
  health,
  healthPlain,
  sections,
  scan,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onRunScan,
  loadingScan,
  lastVerification,
  needsFullScanAfterFix,
  selectedCase,
  onCloseCase,
  onRevertCase,
  reverting,
  findingIndex,
  onFindingIndexChange,
  expandedTechnicalKey,
  onToggleTechnical,
  onFix,
  activeFindingKey,
  renderFixWizard,
  catalogError,
  onLoadCatalog,
  loadingCatalog,
}) {
  const displayIntro = intro || DEFAULT_INTRO;

  return (
    <>
      <div className="admin-panel accounting-intro-box mb-3">
        <h5 className="mb-2">{displayIntro.title}</h5>
        <p className="small mb-2">{displayIntro.body}</p>
        {displayIntro.how_to?.length > 0 && (
          <ol className="small mb-0 ps-3">
            {displayIntro.how_to.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        )}
      </div>

      {catalogError && (
        <div className="admin-alert-item warning mb-3">
          <strong>Fix catalog could not load</strong>
          <p className="small mb-2">{catalogError}</p>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={onLoadCatalog} disabled={loadingCatalog}>
            {loadingCatalog ? 'Loading…' : 'Retry load fix catalog'}
          </button>
        </div>
      )}

      {health && (
        <div className="admin-panel accounting-health-grid mb-3">
          <div className="accounting-health-score">
            <div className={`score-circle ${health.score >= 80 ? 'ok' : health.score >= 50 ? 'warn' : 'bad'}`}>{health.score}</div>
            <div className="small fw-semibold">{healthPlain?.score_label || 'Account health score'}</div>
            <div className="small text-muted">{healthPlain?.score_message || health.label}</div>
          </div>
          {health.bs_pl && (
            <>
              <div className="accounting-metric-card">
                <span className="small text-muted">{healthPlain?.bs_label || 'Balance sheet result'}</span>
                <strong>{formatMoney(health.bs_pl.bs_net)}</strong>
              </div>
              <div className="accounting-metric-card">
                <span className="small text-muted">{healthPlain?.pl_label || 'Profit & loss result'}</span>
                <strong>{formatMoney(health.bs_pl.pl_net)}</strong>
              </div>
              <div className="accounting-metric-card">
                <span className="small text-muted">{healthPlain?.diff_label || 'Mismatch (should be ₹0)'}</span>
                <strong className={health.bs_pl.balanced ? 'text-success' : 'text-danger'}>{formatMoney(health.bs_pl.difference)}</strong>
                {healthPlain?.diff_message && (
                  <span className="small d-block mt-1 text-muted">{healthPlain.diff_message}</span>
                )}
              </div>
            </>
          )}
          <div className="accounting-metric-card">
            <span className="small text-muted">Must fix soon</span>
            <strong>{health.finding_counts?.critical ?? 0}</strong>
          </div>
          <div className="accounting-metric-card">
            <span className="small text-muted">Review when you can</span>
            <strong>{health.finding_counts?.warning ?? 0}</strong>
          </div>
        </div>
      )}

      {lastVerification && (
        <div className={`admin-alert-item mb-3 ${lastVerification.verified_ok ? 'info' : 'warning'}`}>
          <strong>{lastVerification.plain?.headline || 'After your last fix'}</strong>
          <p className="small mb-0 mt-1">
            {lastVerification.plain?.detail || (lastVerification.bs_pl_balanced
              ? 'Balance sheet and profit report match.'
              : 'Balance sheet and profit report still do not match.')}
            {' '}
            {lastVerification.plain?.remaining || `${lastVerification.remaining_critical} serious issue(s) still showing.`}
          </p>
          {needsFullScanAfterFix && (
            <p className="small mb-0 mt-2">
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={onRunScan} disabled={loadingScan}>
                Run full integrity scan
              </button>
              {' '}
              Large fixes only verify BS/PL balance — run a full scan to refresh all findings.
            </p>
          )}
        </div>
      )}

      {selectedCase && (
        <CorrectionCaseLog
          caseRecord={selectedCase}
          onClose={onCloseCase}
          onRevert={onRevertCase}
          reverting={reverting}
        />
      )}

      <div className="admin-panel">
        <h5>Integrity scan</h5>
        <p className="small text-muted mb-2">
          One scan at a time — checks balance sheet vs profit, fuel costs, stock, gas, and cash for the selected period.
          Default range: operation start through last day closing.
        </p>
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label small">From date</label>
            <input type="date" className="form-control" value={fromDate} onChange={(e) => onFromDateChange(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label small">To date</label>
            <input type="date" className="form-control" value={toDate} onChange={(e) => onToDateChange(e.target.value)} />
          </div>
          <div className="col-md-4">
            <button type="button" className="btn btn-primary" onClick={onRunScan} disabled={loadingScan}>
              {loadingScan ? 'Scanning…' : 'Run integrity scan'}
            </button>
          </div>
        </div>
        {loadingScan && (
          <div className="accounting-scan-progress mt-3">
            <p className="small fw-semibold mb-1">Scan in progress</p>
            <p className="small text-muted mb-0">
              Checking books section by section on the server. Large pumps may take up to 2 minutes — please wait.
            </p>
          </div>
        )}
      </div>

      {sections.length > 0 && (
        <AccountingFindingNavigator
          sections={sections}
          currentIndex={findingIndex}
          onIndexChange={onFindingIndexChange}
          expandedTechnicalKey={expandedTechnicalKey}
          onToggleTechnical={onToggleTechnical}
          onFix={onFix}
          renderFixWizard={renderFixWizard}
          activeFindingKey={activeFindingKey}
        />
      )}

      {scan && sections.length === 0 && (
        <div className="admin-alert-item info">
          No problems found for {fromDate} to {toDate}. Your accounts look good for this period.
        </div>
      )}
    </>
  );
}
